import React, { useEffect, useState } from 'react';
import { Card, Row, Col, Select, Button, Table, Badge, List, Grid, Typography, Input, Tag } from 'antd';
import { ExportOutlined, RightOutlined, SearchOutlined } from '@ant-design/icons';
import { useFindAllEntityMasterMutation } from '../../redux-toolkit/services/entityMasterService';
import { useGetDashboardDataMutation } from '../../redux-toolkit/services/dashboardService';
import { useGetProjectPhaseActivitiesMutation, useFindAllOpportunityApplicationMutation, useSearchProjectPhaseActivitiesMutation } from '../../redux-toolkit/services/opportunityApplicationService';
import { getLocalStorageData } from '../../utils/LocalStorageService';
import './dashboard.scss';

const { Option } = Select;
const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

export const RISK_VALUES = [
    { id: 0, name: "Critical", color: "#f5222d" },
    { id: 1, name: "Very high", color: "#f5222d" },
    { id: 2, name: "High", color: "#faad14" },
    { id: 3, name: "Medium", color: "#1890ff" },
    { id: 4, name: "Low", color: "#52c41a" },
];

const ProjectsDashboard = () => {
    const screens = useBreakpoint();
    const [selectedEntity, setSelectedEntity] = useState<string | null>(null);
    const [entities, setEntities] = useState<any[]>([]);

    // Local filters
    const [searchText, setSearchText] = useState('');
    const [ownerFilter, setOwnerFilter] = useState('All Owners');

    const localUserObj: any = getLocalStorageData("localUser");
    const token = localUserObj?.token;

    // API Hooks
    const [findAllEntities, { data: allEntitiesData, isLoading: isEntitiesLoading }] = useFindAllEntityMasterMutation();
    const [getDashboardData, { data: dashboardData, isLoading: isDashboardLoading }] = useGetDashboardDataMutation();
    const [findAllOpportunityApplications, { data: allOpportunityAppData, isLoading: isOppLoading }]: any = useFindAllOpportunityApplicationMutation();
    const [getProjectPhaseActivities, { data: projectPhaseActivitiesData, isLoading: isProjectPhaseLoading }] = useGetProjectPhaseActivitiesMutation();
    const [searchProjectPhaseActivities, { data: searchResultData, isLoading: isSearchLoading }] = useSearchProjectPhaseActivitiesMutation();

    useEffect(() => {
        if (token) {
            findAllEntities({ token });
            findAllOpportunityApplications({ token });
        }
    }, [token]);

    useEffect(() => {
        if (allOpportunityAppData && token) {
            // Extract unique externalProjectId from applications
            const projectIds = Array.from(new Set(
                allOpportunityAppData
                    .map((app: any) => app.externalProjectId)
                    .filter(Boolean)
            ));

            if (projectIds.length > 0) {
                getProjectPhaseActivities({
                    payload: projectIds,
                    page: 0,
                    size: 100, // Fetch more for dashboard summary
                    token
                });
            }
        }
    }, [allOpportunityAppData, token]);

    useEffect(() => {
        if (allEntitiesData?.data) {
            setEntities(allEntitiesData.data);
            if (!selectedEntity && allEntitiesData.data.length > 0) {
                setSelectedEntity(allEntitiesData.data[0].id);
            }
        }
    }, [allEntitiesData]);

    useEffect(() => {
        if (selectedEntity && token) {
            getDashboardData({ payload: { entityId: selectedEntity }, token });
        }
    }, [selectedEntity, token]);

    useEffect(() => {
        if (projectPhaseActivitiesData) {
            console.log("Project Phase Activities Data:", projectPhaseActivitiesData);
        }
    }, [projectPhaseActivitiesData]);

    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            if (token && searchText.trim()) {
                searchProjectPhaseActivities({
                    payload: {
                        searchBy: searchText,
                        pageNumber: 0,
                        offset: 100
                    },
                    token
                });
            }
        }, 500);

        return () => clearTimeout(delayDebounceFn);
    }, [searchText, token]);

    const handleEntityChange = (value: string) => {
        setSelectedEntity(value);
    };

    // Helper: Calculate Timeline Status
    const calculateTimelineStatus = (record: any) => {
        const { planEndDate, actualEndDate, projectStatus } = record;
        const now = new Date();
        const pEnd = planEndDate ? new Date(planEndDate) : null;
        const aEnd = actualEndDate ? new Date(actualEndDate) : null;

        if (aEnd && pEnd) {
            return aEnd > pEnd ? 'Delayed' : 'On Track';
        }
        if (pEnd && pEnd < now && projectStatus !== 'Completed') {
            return 'At Risk';
        }
        return projectStatus || 'On Track';
    };

    // Helper: Map Risk Level
    const getRiskDisplay = (riskLevel: string) => {
        const risk = RISK_VALUES.find(r => r.name.toLowerCase() === (riskLevel || '').toLowerCase());
        return risk ? { name: risk.name, color: risk.color } : { name: riskLevel || 'Low', color: '#52c41a' };
    };

    // Derived Data
    const currentEntity = entities.find(e => e.id === selectedEntity);
    const entityName = currentEntity?.entityName || 'Select Entity';

    // Use search results if searchText is present, otherwise use initial load
    const projectsData: any = (searchText.trim() && searchResultData)
        ? searchResultData.data
        : (projectPhaseActivitiesData?.data || []);

    const filteredProjects = projectsData.filter((item: any) => {
        // Local filtering only for owner now, since project/owner text search is done server-side
        const matchesOwner = ownerFilter === 'All Owners' || item.taskOwner === ownerFilter;
        return matchesOwner;
    });

    // Map projects to their related entities from opportunity data
    const projectEntityMap = React.useMemo(() => {
        const mapping: any = {};
        if (allOpportunityAppData) {
            allOpportunityAppData.forEach((app: any) => {
                if (app.externalProjectId) {
                    mapping[app.externalProjectId] = {
                        entityName: app.entityMaster?.name || 'N/A',
                        entityType: app.entityTypeMaster?.name || app.entityMaster?.entityType?.name || 'N/A'
                    };
                }
            });
        }
        return mapping;
    }, [allOpportunityAppData]);

    const renderKPIBadge = (score: number) => {
        let color = '#52c41a'; // 5
        if (score === 4) color = '#1890ff';
        if (score === 3) color = '#faad14';
        if (score < 3) color = '#f5222d';
        return (
            <div style={{
                width: '28px', height: '28px', borderRadius: '50%', backgroundColor: color,
                color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontWeight: 'bold', margin: '0 auto'
            }}>
                {score}
            </div>
        );
    };

    const renderTimelineTag = (status: string) => {
        let color = 'default';
        if (status === 'On Track') color = 'success';
        if (status === 'Delayed') color = 'warning';
        if (status === 'At Risk') color = 'error';
        return <Tag color={color} style={{ minWidth: '80px', textAlign: 'center' }}>{status}</Tag>;
    };

    const renderRiskScore = (riskLevel: string) => {
        const risk = getRiskDisplay(riskLevel);
        return <Text style={{ color: risk.color, fontWeight: 'bold' }}>{risk.name}</Text>;
    };

    // Derived Metrics from API data
    const onTrackCount = filteredProjects.filter((p: any) => calculateTimelineStatus(p) === 'On Track').length;
    const delayedCount = filteredProjects.filter((p: any) => calculateTimelineStatus(p) === 'Delayed').length;
    const atRiskCount = filteredProjects.filter((p: any) => calculateTimelineStatus(p) === 'At Risk').length;

    // Avg KPI calculation based on percentageCompleted
    const completedPercentages = filteredProjects
        .map((p: any) => p.percentageCompleted)
        .filter((val: any) => val !== null && val !== undefined);
    const avgKPI = completedPercentages.length > 0
        ? (completedPercentages.reduce((acc: number, curr: number) => acc + curr, 0) / completedPercentages.length).toFixed(1) + "%"
        : "N/A";

    // Sidebar Mock Data
    const alerts = [
        { id: 1, text: 'Project at risk: Retail OmniPilot (Isha Ambani) — requires attention.', type: 'warning' },
        { id: 2, text: 'Project at risk: Green Hydrogen Pilot (Anant Ambani) — requires attention.', type: 'warning' },
    ];
    const quickStats = [
        { label: 'Heirs', value: 5, color: '#e6f7ff', textColor: '#1890ff' },
        { label: 'Assets', value: 4, color: '#f6ffed', textColor: '#52c41a' },
        { label: 'Projects', value: 3, color: '#fff7e6', textColor: '#fa8c16' },
        { label: 'Audits', value: 3, color: '#fff1f0', textColor: '#f5222d' },
    ];

    return (
        <div className="p-4" style={{ backgroundColor: '#f0f2f5', minHeight: '100vh' }}>
            {/* Header Section */}
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <Title level={2} style={{ marginBottom: 0 }}>
                        {entityName} — Reliance Family Office
                    </Title>
                    <Text type="secondary">Single master dashboard • Succession • Wealth • Projects • CAP Table • Audits • Mentorship</Text>
                    <div style={{ marginTop: '8px' }} className="d-flex align-items-center text-muted">
                        <span>Dashboard</span> <RightOutlined style={{ fontSize: '10px', margin: '0 8px' }} /> <span className="text-primary">Projects</span>
                    </div>
                </div>
                <div className="d-flex gap-3">
                    <Select
                        showSearch
                        placeholder="Search across dashboard..."
                        optionFilterProp="children"
                        onChange={handleEntityChange}
                        onSearch={(val) => console.log('search:', val)}
                        filterOption={(input, option) =>
                            (option?.children as unknown as string).toLowerCase().includes(input.toLowerCase())
                        }
                        style={{ width: 300 }}
                        value={selectedEntity}
                        loading={isEntitiesLoading}
                    >
                        {entities.map(e => (
                            <Option key={e.id} value={e.id}>{e.entityName}</Option>
                        ))}
                    </Select>
                    <Button type="primary" icon={<ExportOutlined />}>Export / Print</Button>
                </div>
            </div>

            {/* Main Content */}
            <Row gutter={[24, 24]}>
                {/* Left Column - Projects Table & Metrics */}
                <Col span={24}>
                    <Card className="mb-4 shadow-sm" bordered={false} bodyStyle={{ padding: '24px' }}>
                        <div className="d-flex justify-content-between align-items-center mb-4">
                            <Title level={4} className="mb-0">Projects & Opportunities</Title>
                            <div className="d-flex gap-2">
                                <Input
                                    prefix={<SearchOutlined />}
                                    placeholder="Search project..."
                                    style={{ width: 200 }}
                                    onChange={(e) => setSearchText(e.target.value)}
                                />
                                <Select defaultValue="All Owners" onChange={setOwnerFilter} style={{ width: 140 }}>
                                    <Option value="All Owners">All Owners</Option>
                                    {Array.from(new Set(projectsData.map((p: any) => p.taskOwner).filter(Boolean))).map((owner: any) => (
                                        <Option key={owner} value={owner}>{owner}</Option>
                                    ))}
                                </Select>
                                <Button type="default" className="bg-success text-white border-success">Export CSV</Button>
                            </div>
                        </div>

                        <Text type="secondary" className="mb-3 d-block">Showing {filteredProjects.length} of {projectsData.length} projects</Text>

                        <Table
                            dataSource={filteredProjects}
                            pagination={{ pageSize: 5 }}
                            loading={isProjectPhaseLoading || isSearchLoading}
                            rowKey="id"
                            columns={[
                                { title: 'PROJECT', dataIndex: 'projectName', key: 'projectName', render: (text) => <b>{text}</b> },
                                {
                                    title: 'RELATED ENTITY',
                                    key: 'relatedEntity',
                                    render: (_, record: any) => <span>{projectEntityMap[record.projectId]?.entityName || 'N/A'}</span>
                                },
                                {
                                    title: 'ENTITY TYPE',
                                    key: 'entityType',
                                    render: (_, record: any) => <Tag color="blue">{projectEntityMap[record.projectId]?.entityType || 'N/A'}</Tag>
                                },
                                { title: 'OWNER', dataIndex: 'taskOwner', key: 'taskOwner', render: (text) => <Text type="secondary">{text}</Text> },
                                { title: 'KPI ALIGN', dataIndex: 'kraMasterName', key: 'kraMasterName', align: 'center', render: (text) => <Text>{text || 'N/A'}</Text> },
                                { title: 'TIMELINE', key: 'timeline', align: 'center', render: (_, record) => renderTimelineTag(calculateTimelineStatus(record)) },
                                { title: 'RISK LEVEL', dataIndex: 'riskLevel', key: 'riskLevel', align: 'center', render: (riskLevel: any) => renderRiskScore(riskLevel) },
                                { title: 'RISK DETAIL', dataIndex: 'risk', key: 'risk', render: (text: any) => <Text type="secondary">{text || 'N/A'}</Text> },
                                { title: 'ACTION', key: 'action', align: 'center', render: () => <a href="#">View</a> }
                            ]}
                        />

                        {/* Summary Metrics */}
                        <Row gutter={16} className="mt-4">
                            <Col span={6}>
                                <div className="p-3 rounded" style={{ backgroundColor: '#f6ffed' }}>
                                    <Text type="secondary" className="d-block mb-1">On Track</Text>
                                    <Title level={3} className="text-success m-0">{onTrackCount}</Title>
                                </div>
                            </Col>
                            <Col span={6}>
                                <div className="p-3 rounded" style={{ backgroundColor: '#fffbe6' }}>
                                    <Text type="secondary" className="d-block mb-1">Delayed</Text>
                                    <Title level={3} className="text-warning m-0">{delayedCount}</Title>
                                </div>
                            </Col>
                            <Col span={6}>
                                <div className="p-3 rounded" style={{ backgroundColor: '#fff1f0' }}>
                                    <Text type="secondary" className="d-block mb-1">At Risk</Text>
                                    <Title level={3} className="text-danger m-0">{atRiskCount}</Title>
                                </div>
                            </Col>
                            <Col span={6}>
                                <div className="p-3 rounded" style={{ backgroundColor: '#f0f5ff' }}>
                                    <Text type="secondary" className="d-block mb-1">Avg KPI</Text>
                                    <Title level={3} className="text-primary m-0">{avgKPI}</Title>
                                </div>
                            </Col>
                        </Row>
                    </Card>
                </Col>
            </Row>
        </div>
    );
};

export default ProjectsDashboard;
